import { useEffect } from 'react';
import { FormMessage } from '@/bundle/_OrgAdmin/shared/ui/styles';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { useMutation } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { UPDATE_USER_MOBILE_PHONE_NOTIFICATION } from '@/const/shared';
import { AssignedWireType } from '@/types/wireTypes';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { ASSIGN_USER_INITIAL_VALUES, ASSIGN_USER_SCHEMA } from '@/bundle/_OrgAdmin/shared/const/const';
import { getChangedFormValues } from '@/helpers/formHelpers/formHelpers';
import { updateRecipientByProxy } from '@/bundle/_Recipient/_Details/ui/ProxyEditRecipientForm/api/api';

type FormValues = {
  mobile_phone: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
};

type ProxyEditRecipientFormType = {
  wire: AssignedWireType;
  onClose: () => void;
  onSave: () => void;
};

export const ProxyEditRecipientForm = ({ wire, onClose, onSave }: ProxyEditRecipientFormType) => {
  const recipient = wire?.assigned_user;

  const { data, mutate, isPending } = useMutation({
    mutationKey: ['update_recipient_by_proxy'],
    mutationFn: (payload: FormValues) => {
      const userId = wire?.assigned_user?.id;

      return updateRecipientByProxy(wire?.id, userId, payload);
    },
    onSuccess(response) {
      if (response?.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.OutboundWireProxyEditRecipientSuccess);
      showSnackbar(UPDATE_USER_MOBILE_PHONE_NOTIFICATION, { maxWidth: '375px' });

      onClose();
      onSave();
    },
  });

  const formik = useFormik({
    initialValues: ASSIGN_USER_INITIAL_VALUES,
    validationSchema: ASSIGN_USER_SCHEMA,
    onSubmit: (values) => {
      const { first_name, last_name, middle_name, email, mobile_phone } = recipient;
      const initialUser = {
        first_name,
        middle_name,
        last_name,
        email,
        mobile_phone,
      };

      const changedPayload = getChangedFormValues<FormValues>(values, initialUser);

      mutate(changedPayload);
    },
  });

  useEffect(() => {
    const { first_name, last_name, middle_name, email } = recipient;

    formik.setValues({
      first_name,
      last_name,
      middle_name,
      email,
      mobile_phone: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipient]);

  const apiError = data?.error;

  return (
    <DetailsWidget header='Edit Recipient' hasHighlight headerOffset={8}>
      <FormMessage>
        Any change to the recipient info below will be applied to the other wires this pending account is assigned to.
      </FormMessage>
      <FormikForm value={formik}>
        <FormikInput name='email' label='Email Address*' disabled />
        <FormikPhoneInput name='mobile_phone' label='Mobile Phone Number*' apiError={apiError} />
        <FormikInput name='first_name' label='First Name*' placeholder='Enter First Name' apiError={apiError} />
        <FormikInput name='middle_name' label='Middle Name' placeholder='Enter Middle Name' apiError={apiError} />
        <FormikInput name='last_name' label='Last Name*' placeholder='Enter Last Name' apiError={apiError} />

        <Box flexDirection='row' width='100%' justifyContent='end' columnGap='12px' marginTop='12px'>
          <Button variant='secondary' onClick={onClose} width={172} size='medium'>
            Cancel
          </Button>
          <Button type='submit' isLoading={isPending} width={172} size='medium'>
            Save
          </Button>
        </Box>
      </FormikForm>
    </DetailsWidget>
  );
};
